/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled, { css } from "styled-components";
import { Link } from "react-router";

import { ThemePalette, ThemeProps } from "@src/components/Theme";
// The Ops Console's own brand files (nutanix_portal/public/assets): the logo, its light-on-dark
// variant (the website's brand-dark) for the dark theme, and the favicon mark for the folded sidebar.
import tech9iqLogo from "./images/tech9iq-logo.png";
// import tech9iqLogoDark from "./images/tech9iq-logo-dark.webp";
// import tech9iqMark from "./images/tech9iq-favicon.svg";
// 2026-10-07 (logo pass): the colourful logo on dark too, the sign-in app's dark variant (Customer-Login
// apps/web/public/assets/tech9iq-logo-dark.png): coloured lettering, only the tagline lightened.
import tech9iqLogoDark from "./images/tech9iq-logo-dark.png";
// 2026-10-07: the folded sidebar shows the browser tab's own icon (public/favicon.ico, index.html's
// first <link rel="icon">, the Ops Console's tech9iq.ico) rather than the "iq" crop.
const tech9iqMark = "/favicon.ico";
// Were a redrawn SVG logo and mark; replaced by the Ops Console's files above.
// import tech9iqLogo from "./images/tech9iq-logo.svg";
// import tech9iqMark from "./images/tech9iq-mark.svg";

/*
 * The Swift lockup: the official Tech9IQ logo, a hairline divider and the product name - the same
 * pattern as the Ops Console ("tech9iQ | Ops Console") and AWS Management, whose sidebar puts the
 * organisation under the product name (`subtitle`, here the current project). `large` is the
 * sign-in size, `small` the sidebar size (34 px logo, 14 px name), `mark` the folded sidebar.
 */
type Size = "large" | "small" | "mark";

const Lockup = styled.div<{ size: Size }>`
  display: flex;
  align-items: center;
  gap: ${props => (props.size === "large" ? "18px" : "8px")};
  min-width: 0;
`;
const LogoImg = styled.img<{ size: Size }>`
  display: block;
  flex-shrink: 0;
  ${props =>
    props.size === "large"
      ? css`
          height: 60px;
        `
      : props.size === "mark"
        ? css`
            height: 28px;
          `
        : css`
            height: 34px;
          `}
`;
/*
 * No plate behind the logo: the light file on the light theme; the light-on-dark one (black
 * background, blended away with "screen") on the dark theme.
 * 2026-10-07 (logo pass): the dark one is the sign-in app's transparent PNG, so no blending (was
 * mix-blend-mode: screen on .logo-dark).
 */
const LogoPair = styled.span<{ size: Size }>`
  display: inline-flex;
  flex-shrink: 0;
  .logo-dark {
    display: none;
  }
  /* Was ":root[data-theme="dark"] & .logo-light / .logo-dark": styled-components glues a selector
     that starts with ":" onto the component (".x:root[…] .x .logo-light"), so it never matched and
     the dark theme kept the light logo. html[data-theme] is the same switch. */
  html[data-theme="dark"] & .logo-light {
    display: none;
  }
  html[data-theme="dark"] & .logo-dark {
    display: block;
  }
`;
const Divider = styled.span<{ size: Size }>`
  width: 1px;
  flex-shrink: 0;
  height: ${props => (props.size === "large" ? "44px" : "20px")};
  background: ${ThemePalette.grayscale[0]};
`;
const Names = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const Product = styled.span<{ size: Size }>`
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  font-size: ${props => (props.size === "large" ? "32px" : "14px")};
  line-height: ${props => (props.size === "large" ? "40px" : "20px")};
  letter-spacing: ${props => (props.size === "large" ? "-0.01em" : "0")};
  color: ${ThemePalette.black};
  white-space: nowrap;
`;
const Subtitle = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const LinkStyled = styled(Link)`
  display: block;
  min-width: 0;
  border-radius: 6px;
  transition: all ${ThemeProps.animations.swift};
  text-decoration: none;
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
`;

type Props = {
  small?: boolean;
  smallblack?: boolean;
  large?: boolean;
  mark?: boolean;
  /** A small second line under the product name (the sidebar shows the current project). */
  subtitle?: string | null;
  customRef?: (ref: HTMLElement) => void;
  to?: string;
  className?: string;
};

class Logo extends React.Component<Props> {
  render() {
    const { to = "/", large, small, smallblack, mark, className, customRef, subtitle } =
      this.props;
    const size: Size = mark ? "mark" : large && !small && !smallblack ? "large" : "small";
    const normalizedTo = to.startsWith("/") ? to : `../${to}`;
    const lockup =
      size === "mark" ? (
        <Lockup size={size}>
          <LogoImg size={size} src={tech9iqMark} alt="Tech9IQ" />
        </Lockup>
      ) : (
        <Lockup size={size}>
          <LogoPair size={size}>
            <LogoImg className="logo-light" size={size} src={tech9iqLogo} alt="Tech9IQ" />
            <LogoImg className="logo-dark" size={size} src={tech9iqLogoDark} alt="Tech9IQ" />
          </LogoPair>
          <Divider size={size} />
          <Names>
            <Product size={size}>SwiftIQ</Product>
            {subtitle && size === "small" ? (
              <Subtitle title={`Project: ${subtitle}`}>{subtitle}</Subtitle>
            ) : null}
          </Names>
        </Lockup>
      );
    return (
      <div
        style={{ transition: `all ${ThemeProps.animations.swift}`, minWidth: 0 }}
        className={className}
        ref={ref => {
          if (customRef && ref) customRef(ref);
        }}
      >
        {normalizedTo ? (
          <LinkStyled to={normalizedTo} aria-label="SwiftIQ overview">
            {lockup}
          </LinkStyled>
        ) : (
          lockup
        )}
      </div>
    );
  }
}

export default Logo;
